import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import { Camera, X, Check, Loader2 } from "lucide-react";

// TEMP DEBUG BANNER — delete this component + its usage below once you've
// confirmed this exact file is what's being served. If you don't see the
// bright orange bar at the top of the Post Listing page, the app is still
// running the OLD file — this is a deploy/cache issue, not a code issue.
function DebugBuildBanner() {
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, zIndex:99999, background:"#f59e0b", color:"#000", padding:"8px 12px", fontSize:12, fontWeight:800, textAlign:"center", letterSpacing:".02em" }}>
      🟠 DEBUG BUILD ACTIVE — PostListing.jsx v2026-07-13-d (+ Heavy Equipment & Personal Items categories) — remove DebugBuildBanner once confirmed
    </div>
  );
}

// ── Vehicle data ─────────────────────────────────────────────────────
// Dynamic year range so it never goes stale (always current model year + 1 down to 30 yrs back)
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({length:32},(_,i)=>(CURRENT_YEAR+1-i).toString());

const VEHICLE_MODELS = {
  Acura:         ["MDX","RDX","TLX","Integra","ILX"],
  Alfa_Romeo:    ["Giulia","Stelvio","4C"],
  Aston_Martin:  ["DB11","Vantage"],
  Audi:          ["A3","A4","A6","Q3","Q5","Q7","e-tron"],
  Bentley:       ["Continental","Bentayga"],
  BMW:           ["1 Series","3 Series","5 Series","X3","X5","X7"],
  Buick:         ["Enclave","Encore","Encore GX","Envision","LaCrosse","Regal"],
  Cadillac:      ["Escalade","XT4","XT5","XT6","CT4","CT5"],
  Chevrolet:     ["Silverado 1500","Silverado 2500HD","Silverado 3500HD","Silverado 4500HD/5500HD/6500HD","Colorado","Tahoe","Suburban","Blazer","Equinox","Traverse","Express Van","Express Cutaway","Malibu","Camaro","Impala","Cruze","Spark","Bolt EV","Trailblazer","Trax"],
  Chrysler:      ["Pacifica","Voyager","300"],
  Datsun:        ["240Z","280Z","Pickup"],
  Dodge:         ["Durango","Charger","Challenger","Journey","Grand Caravan","Dart"],
  Ferrari:       ["488","Portofino","Roma"],
  Fiat:          ["500","500X"],
  Ford:          ["F-150","F-250 Super Duty","F-350 Super Duty","F-450 Super Duty","F-550 Super Duty","F-650","F-750","Ranger","Bronco","Bronco Sport","Expedition","Explorer","Edge","Escape","Transit","Transit Connect","E-Series","Mustang","Fusion","Focus","Taurus","EcoSport","Maverick"],
  Freightliner:  ["Sprinter","M2 106","Cascadia"],
  Genesis:       ["GV70","GV80","G70"],
  Geo:           ["Metro","Tracker","Prizm"],
  GMC:           ["Sierra 1500","Sierra 2500HD","Sierra 3500HD","Sierra 4500HD/5500HD/6500HD","Canyon","Yukon","Yukon XL","Savana","Terrain","Acadia"],
  Harley_Davidson:["Road King","Street Glide","Sportster","Fat Boy"],
  Honda:         ["Ridgeline","Pilot","Passport","CR-V","HR-V","Odyssey","Civic","Accord","Fit","Insight"],
  Hyundai:       ["Tucson","Santa Fe","Palisade","Santa Cruz","Elantra","Sonata","Accent","Venue","Kona","Ioniq 5"],
  Infiniti:      ["QX60","QX80","Q50"],
  International: ["MV Series","CV Series","DuraStar"],
  Isuzu:         ["NPR","NRR","FTR"],
  Jaguar:        ["F-Pace","XF","F-Type"],
  Jeep:          ["Gladiator","Wrangler","Grand Cherokee","Cherokee","Compass","Renegade","Wagoneer","Grand Wagoneer"],
  Kawasaki:      ["Vulcan","Ninja"],
  Kenworth:      ["T680","T880","T370"],
  Kia:           ["Telluride","Sorento","Sportage","Mohave","Forte","Soul","Rio","Optima/K5","Carnival","EV6"],
  Land_Rover:    ["Range Rover","Discovery","Defender"],
  Lexus:         ["GX","LX","RX","TX","ES","IS","LS","NX","UX","RC"],
  Lincoln:       ["Navigator","Aviator","Corsair","Nautilus","Continental"],
  Lotus:         ["Emira","Evora"],
  Mack:          ["Anthem","Granite","Pinnacle"],
  Maserati:      ["Ghibli","Levante"],
  Mazda:         ["CX-5","CX-9","CX-50","MX-30","Mazda3","Mazda6","MX-5 Miata"],
  Mercedes_Benz: ["Sprinter","Metris","GLE","GLS","GLC","C-Class","E-Class","S-Class"],
  Mercury:       ["Grand Marquis","Mountaineer","Sable"],
  Mini:          ["Cooper","Countryman"],
  Mitsubishi:    ["Outlander","Outlander Sport","Mirage","Eclipse Cross"],
  Nissan:        ["Frontier","Titan","Titan XD","Pathfinder","Armada","Murano","Rogue","NV Cargo","NV200","Altima","Sentra","Maxima","Versa","Kicks","Ariya","370Z"],
  Oldsmobile:    ["Cutlass","Alero","Bravada"],
  Peterbilt:     ["579","567","348"],
  Plymouth:      ["Voyager","Barracuda"],
  Pontiac:       ["Grand Prix","G6","Vibe"],
  Porsche:       ["Cayenne","Macan","911"],
  RAM:           ["1500","2500","3500","4500","5500","ProMaster","ProMaster City","Dakota"],
  Rolls_Royce:   ["Ghost","Cullinan"],
  Saab:          ["9-3","9-5"],
  Saturn:        ["Vue","Ion","Outlook"],
  Scion:         ["tC","xB","FR-S"],
  Smart:         ["Fortwo"],
  Subaru:        ["Outback","Forester","Ascent","Crosstrek","Impreza","Legacy","WRX","BRZ"],
  Suzuki:        ["Boulevard","GSX-R"],
  Tesla:         ["Model 3","Model Y","Model S","Model X","Cybertruck"],
  Toyota:        ["Tacoma","Tundra","4Runner","Sequoia","Highlander","Land Cruiser","RAV4","Camry","Hilux","Sienna","Corolla","Corolla Cross","Prius","Avalon","C-HR","Venza","GR86","Supra"],
  Volkswagen:    ["Atlas","Taos","Tiguan","Crafter","Jetta","Golf","Passat","ID.4"],
  Volvo:         ["VNL","VHD","VAH","XC90","XC60"],
  Western_Star:  ["49X","47X","57X"],
  Willys:        ["Jeep CJ","Wagon"],
  Yamaha:        ["V Star","Bolt","YZF-R6"],
  Other:         ["Other / Not Listed"],
};

// Human-friendly display names for makes with underscores (used as object keys since JS keys can't have spaces easily)
const MAKE_LABELS = {
  Mercedes_Benz:"Mercedes-Benz",
  Western_Star:"Western Star",
  Land_Rover:"Land Rover",
  Harley_Davidson:"Harley-Davidson",
  Alfa_Romeo:"Alfa Romeo",
  Aston_Martin:"Aston Martin",
  Rolls_Royce:"Rolls-Royce",
};
const makeLabel = (m) => MAKE_LABELS[m] || m;

const MAKES = Object.keys(VEHICLE_MODELS)
  .filter(m => m !== "Other")
  .sort((a,b) => makeLabel(a).localeCompare(makeLabel(b)))
  .concat("Other");

// ── Heavy equipment data (the actual oversize-load machinery this industry hauls) ──
const EQUIPMENT_TYPES = ["Backhoe Loader","Excavator","Skidder","Bulldozer/Dozer","Wheel Loader","Motor Grader","Compactor/Roller","Crane","Forklift","Articulated Dump Truck","Forestry Equipment","Other"];

const EQUIPMENT_MODELS = {
  Caterpillar:    ["420 Backhoe Loader","430 Backhoe Loader","D6 Dozer","D8 Dozer","320 Excavator","336 Excavator","950 Wheel Loader","735 Articulated Truck","Other"],
  John_Deere:     ["310L Backhoe Loader","410L Backhoe Loader","848L Skidder","648L Skidder","750K Dozer","350G Excavator","644 Wheel Loader"],
  Case_CE:        ["580 Super N Backhoe Loader","590 Backhoe Loader","1150 Dozer","CX350 Excavator"],
  Komatsu:        ["WA320 Wheel Loader","PC210 Excavator","D65 Dozer"],
  Volvo_CE:       ["EC220 Excavator","L60 Wheel Loader","A25 Articulated Hauler"],
  Kubota:         ["KX Series Excavator","M Series Tractor"],
  Bobcat:         ["S650 Skid Steer","E85 Excavator","B760 Backhoe Loader"],
  Tigercat:       ["635 Skidder","620 Skidder"],
  Timberjack:     ["460 Skidder","Grapple Skidder"],
  Hitachi:        ["ZX350 Excavator","ZW220 Wheel Loader"],
  Doosan:         ["DX350 Excavator","DL250 Wheel Loader"],
  Liebherr:       ["R 936 Excavator","L 550 Wheel Loader"],
  JCB:            ["3CX Backhoe Loader","4CX Backhoe Loader","JS Excavator"],
  New_Holland_CE: ["B95 Backhoe Loader","E215 Excavator"],
  Terex:          ["TLB840 Backhoe Loader","Crawler Dozer"],
  Kobelco:        ["SK350 Excavator"],
  Link_Belt:      ["350 X4 Excavator"],
  Other:          ["Other / Not Listed"],
};

const EQUIPMENT_MAKE_LABELS = {
  John_Deere:"John Deere", Case_CE:"Case", Volvo_CE:"Volvo CE",
  New_Holland_CE:"New Holland", Link_Belt:"Link-Belt",
};
const equipmentMakeLabel = (m) => EQUIPMENT_MAKE_LABELS[m] || m;
const EQUIPMENT_MAKES = Object.keys(EQUIPMENT_MODELS)
  .filter(m => m !== "Other")
  .sort((a,b) => equipmentMakeLabel(a).localeCompare(equipmentMakeLabel(b)))
  .concat("Other");

// ── Real pilot car product categories with smart fields ──────────────
const CATEGORIES = [
  {
    value:"oversize_signs", label:"🚧 Oversize Load Signs", shortLabel:"Signs", icon:"🚧", priority:true,
    desc:"Roof-mounted signs, banners, LED signs",
    fields:[
      {key:"sign_type", label:"Sign Type", type:"chips", options:["Oversize Load","Wide Load","Long Load","Tall Load","Slow Moving","Hazmat","Custom"]},
      {key:"material", label:"Material", type:"chips", options:["Aluminum","Plastic/PVC","LED Electronic","Magnetic","Fabric Banner"]},
      {key:"dimensions", label:"Dimensions (inches)", type:"text", placeholder:"e.g., 18x84"},
      {key:"illuminated", label:"Illuminated / LED", type:"toggle"},
      {key:"retractable", label:"Retractable / Roll-up", type:"toggle"},
    ]
  },
  {
    value:"lights_strobes", label:"💡 Lights & Strobes", shortLabel:"Strobes", icon:"💡", priority:true,
    desc:"Amber strobes, light bars, LED beacons, arrow boards",
    fields:[
      {key:"light_type", label:"Light Type", type:"chips", options:["Amber Strobe","Rotating Beacon","LED Light Bar","Arrow Board","Dash Light","Magnetic Mount","Permanent Mount"]},
      {key:"brand", label:"Brand", type:"text", placeholder:"e.g., Whelen, Federal Signal, SoundOff"},
      {key:"visibility", label:"Visibility Distance", type:"chips", options:["500 ft","1/4 mile","1/2 mile","1 mile+"]},
      {key:"mounting", label:"Mounting Type", type:"chips", options:["Roof Rack","Magnetic","Permanent","Suction Cup","Cab Guard"]},
      {key:"wattage", label:"Wattage / Power", type:"text", placeholder:"e.g., 40W, 12V"},
    ]
  },
  {
    value:"roof_racks_mounts", label:"🔩 Roof Racks & Mounts", shortLabel:"Mounts", icon:"🔩",
    desc:"Sign mounts, roof racks, cab guard mounts",
    fields:[
      {key:"mount_type", label:"Mount Type", type:"chips", options:["Roof Rack","Cab Guard Mount","Suction Cup & Hook","Magnetic Base","Truck Bed Mount","Van/SUV Universal"]},
      {key:"brand", label:"Brand", type:"text", placeholder:"e.g., Barney's, Pro-Escort"},
      {key:"vehicle_fit", label:"Vehicle Compatibility", type:"chips", options:["Pickup Truck","SUV","Sedan","Minivan","All Vehicles"]},
      {key:"material", label:"Material", type:"chips", options:["Steel","Aluminum","Stainless Steel","Heavy Duty Plastic"]},
      {key:"adjustable", label:"Adjustable / Universal Fit", type:"toggle"},
    ]
  },
  {
    value:"cb_radios_comm", label:"📻 CB Radios & Communication", shortLabel:"Communication", icon:"📻", priority:true,
    desc:"CB radios, antennas, headsets, mounts",
    fields:[
      {key:"comm_type", label:"Type", type:"chips", options:["CB Radio","40-Channel CB","SSB CB Radio","Antenna","Coax Cable","Headset","Radio Mount","Scanner"]},
      {key:"brand", label:"Brand", type:"text", placeholder:"e.g., Cobra, Uniden, Midland, President"},
      {key:"channels", label:"Channels", type:"chips", options:["40 Channel","40 CH + Weather","SSB","GMRS/FRS"]},
      {key:"watts", label:"Output Power", type:"chips", options:["4W Standard","12W SSB","40W Modified"]},
      {key:"includes_antenna", label:"Antenna Included", type:"toggle"},
    ]
  },
  {
    value:"flags_safety", label:"🚩 Flags & Safety Equipment", shortLabel:"Flags & Safety", icon:"🚩",
    desc:"Warning flags, safety vests, cones, first aid",
    fields:[
      {key:"item_type", label:"Item Type", type:"chips", options:["Warning Flags","Safety Vest","Traffic Cones","Slow/Stop Paddle","First Aid Kit","Fire Extinguisher","Reflective Triangles","Hard Hat","Safety Glasses"]},
      {key:"flag_color", label:"Flag Color (if flags)", type:"chips", options:["Orange","Red","Yellow","Green","N/A"]},
      {key:"hi_vis_rating", label:"Hi-Vis Rating", type:"chips", options:["ANSI Class 2","ANSI Class 3","Not Rated","N/A"]},
      {key:"quantity", label:"Quantity in Lot", type:"text", placeholder:"e.g., 12 flags, 2 vests"},
    ]
  },
  {
    value:"height_poles", label:"📏 Height Poles & Measuring", shortLabel:"Measuring", icon:"📏",
    desc:"Measuring sticks, height poles, load gauges",
    fields:[
      {key:"pole_type", label:"Type", type:"chips", options:["Telescoping Height Pole","Fixed Height Pole","Fiberglass Pole","Aluminum Pole","Measuring Tape","Load Gauge"]},
      {key:"max_height", label:"Max Measurement", type:"chips", options:["14 ft","16 ft","18 ft","20 ft","22 ft+","Custom"]},
      {key:"brand", label:"Brand", type:"text", placeholder:"e.g., AMC, Oversize Load Superstore"},
      {key:"collapsible", label:"Collapsible / Travel-ready", type:"toggle"},
    ]
  },
  {
    value:"vehicles", label:"🚗 Cars & Trucks", shortLabel:"Cars & Trucks", icon:"🚗",
    desc:"Pilot cars, pickups, SUVs & escort-ready vehicles",
    fields:[
      {key:"vehicle_type", label:"Vehicle Type", type:"chips", options:["Car","Truck","SUV","Van","Trailer","Motorcycle","Other"]},
      {key:"year",    label:"Year",    type:"vehicle_year"},
      {key:"make",    label:"Make",    type:"vehicle_make"},
      {key:"model",   label:"Model",   type:"vehicle_model"},
      {key:"mileage", label:"Mileage", type:"text", placeholder:"e.g., 45,000"},
      {key:"already_equipped", label:"Already Equipped (signs, lights, CB)", type:"toggle"},
      {key:"gvwr", label:"GVWR Class", type:"chips", options:["1/4 Ton","1/2 Ton","3/4 Ton","1 Ton","2,000+ lbs Min"]},
    ]
  },
  {
    value:"heavy_equipment", label:"🚜 Heavy Equipment & Machinery", shortLabel:"Heavy Equipment", icon:"🚜",
    desc:"Backhoes, excavators, skidders & other oversize-load machinery",
    fields:[
      {key:"equipment_type", label:"Equipment Type", type:"chips", options:EQUIPMENT_TYPES},
      {key:"year",   label:"Year",  type:"equipment_year"},
      {key:"make",   label:"Make",  type:"equipment_make"},
      {key:"model",  label:"Model", type:"equipment_model"},
      {key:"hours",  label:"Hours (engine/operating)", type:"text", placeholder:"e.g., 3,200"},
      {key:"weight", label:"Operating Weight (lbs)", type:"text", placeholder:"e.g., 45,000"},
      {key:"transport_width", label:"Transport Width", type:"text", placeholder:"e.g., 10' 2\""},
      {key:"requires_escort", label:"Requires Oversize Escort to Transport", type:"toggle"},
    ]
  },
  {
    value:"complete_kits", label:"🎒 Complete Starter Kits", shortLabel:"Kits", icon:"🎒",
    desc:"Full equipment packages for new PEVO/drivers",
    fields:[
      {key:"kit_contents", label:"What's Included", type:"chips", options:["Oversize Sign","Strobe Light","CB Radio","Flags","Safety Vest","Height Pole","Roof Mount","First Aid","All of the above"]},
      {key:"state_compliant", label:"State-Compliant Package", type:"chips", options:["PEVO 13-State","NY Compliant","All 50 States","Buyer Specifies"]},
      {key:"new_or_used", label:"New or Used Items", type:"chips", options:["All New","Mix of New & Used","All Used"]},
    ]
  },
  {
    value:"personal_items", label:"🛋️ Personal & Household Items", shortLabel:"Personal Items", icon:"🛋️",
    desc:"Furniture, electronics, tools & anything else you're selling",
    fields:[
      {key:"item_category", label:"Item Category", type:"chips", options:["Furniture","Electronics","Tools & Hardware","Appliances","Sporting Goods","Home & Garden","Clothing & Gear","Other"]},
      {key:"item_desc", label:"Brief Description", type:"text", placeholder:"What is it?"},
    ]
  },
  {
    value:"other", label:"📦 Other Items", shortLabel:"Other", icon:"📦",
    desc:"Anything else pilot-car related",
    fields:[
      {key:"item_desc", label:"Brief Item Description", type:"text", placeholder:"What is it?"},
    ]
  },
];

// Generic condition set for equipment/gear listings ("Open Box" only makes sense for boxed goods)
const CONDITIONS = ["Open Box","Like New","Good","Fair","For Parts"];
// Vehicle-specific condition set — no "Open Box", since that's not a valid vehicle condition
const VEHICLE_CONDITIONS = ["Excellent","Good","Fair","Needs Work","Salvage / Parts Only"];
const STATES_SHORT = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const ESCORT_TYPES = ["Wide Load","Oversize","Overweight","Overheight","All Types"];
const SHIPPING_OPTS = ["Local Pickup Only","Ships Nationwide","Local + Ships"];

// ── UI primitives ─────────────────────────────────────────
function Label({children,sub}) {
  return (
    <div className="mb-1.5">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{children}</span>
      {sub && <span className="text-[11px] text-slate-400 dark:text-slate-500 ml-2 normal-case tracking-normal">{sub}</span>}
    </div>
  );
}

function Input({placeholder,value,onChange,type="text",prefix}) {
  return (
    <div className="relative">
      {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-slate-500 dark:text-slate-400">{prefix}</span>}
      <input 
        type={type} 
        value={value} 
        onChange={e=>onChange(e.target.value)} 
        placeholder={placeholder}
        className={`w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl py-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all ${prefix ? 'pl-8 pr-4' : 'px-4'}`}
      />
    </div>
  );
}

function Textarea({placeholder,value,onChange,rows=4}) {
  return (
    <textarea 
      value={value} 
      onChange={e=>onChange(e.target.value)} 
      placeholder={placeholder} 
      rows={rows}
      className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all resize-y"
    />
  );
}

function SmallToggle({value,onChange,label}) {
  return (
    <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl mt-2">
      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <div 
        onClick={()=>onChange(!value)} 
        className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors ${value ? 'bg-cyan-500' : 'bg-slate-300 dark:bg-slate-700'}`}
      >
        <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${value ? 'left-6' : 'left-1'}`} />
      </div>
    </div>
  );
}

function Chips({options,selected,onToggle,multi=true}) {
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map(o=>{
        const active = multi ? (selected||[]).includes(o) : selected===o;
        return (
          <div key={o} onClick={()=>onToggle(o)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all border select-none ${
              active 
                ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 border-cyan-500/50' 
                : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800'
            }`}>
            {o}
          </div>
        );
      })}
    </div>
  );
}

function Section({title,icon,children}) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm mb-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 flex items-center justify-center text-lg shadow-sm border border-cyan-100 dark:border-cyan-800/50">
          {icon}
        </div>
        <span className="font-bold text-lg text-slate-900 dark:text-white">{title}</span>
      </div>
      {children}
    </div>
  );
}

function FieldGroup({children}) {
  return <div className="mb-4">{children}</div>;
}

function VehicleDropdown({label, options, value, onChange, placeholder, optionLabels}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <select 
          value={value} 
          onChange={e=>onChange(e.target.value)}
          className={`w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500 transition-all appearance-none cursor-pointer ${value ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}
          style={{
            backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='text-slate-400'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat:"no-repeat", backgroundPosition:"right 16px center"
          }}
        >
          <option value="">{placeholder}</option>
          {options.map(o=><option key={o} value={o}>{optionLabels?.[o] || o}</option>)}
        </select>
      </div>
    </div>
  );
}

// Photo zone
function PhotoZone({photos,onAdd,onRemove}) {
  const ref = useRef();
  const [drag,setDrag] = useState(false);
  const handleFiles = files => {
    Array.from(files).slice(0,8-photos.length).forEach(f=>{
      onAdd({url:URL.createObjectURL(f), name:f.name, file: f});
    });
  };
  return (
    <div>
      <div 
        onClick={()=>ref.current.click()}
        onDragOver={e=>{e.preventDefault();setDrag(true);}}
        onDragLeave={()=>setDrag(false)}
        onDrop={e=>{e.preventDefault();setDrag(false);handleFiles(e.dataTransfer.files);}}
        className={`border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all mb-4 ${
          drag 
            ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
            : 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 hover:border-cyan-400'
        }`}
      >
        <div style={{fontSize:28,marginBottom:6}}>📸</div>
        <div className="font-bold text-sm text-cyan-600 dark:text-cyan-400 mb-1">Tap to add photos</div>
        <div className="text-xs text-slate-500">Up to 8 photos · First photo is cover shot</div>
        <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={e=>handleFiles(e.target.files)}/>
      </div>

      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          {photos.map((p,i)=>(
            <div key={i} className={`relative rounded-xl overflow-hidden aspect-square ${i===0 ? 'ring-2 ring-cyan-500 ring-offset-2 dark:ring-offset-slate-900' : 'border border-slate-200 dark:border-slate-700'}`}>
              <img src={p.url} alt="" className="w-full h-full object-cover"/>
              {i===0 && (
                <div className="absolute top-2 left-2 bg-cyan-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
                  COVER
                </div>
              )}
              <div 
                onClick={()=>onRemove(i)} 
                className="absolute top-2 right-2 w-6 h-6 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center cursor-pointer text-white transition-colors"
              >
                <X className="w-3 h-3" />
              </div>
            </div>
          ))}
          {photos.length < 8 && (
            <div 
              onClick={()=>ref.current.click()}
              className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700 aspect-square flex items-center justify-center cursor-pointer text-slate-400 hover:text-cyan-500 hover:border-cyan-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all bg-slate-50 dark:bg-slate-900"
            >
              <span className="text-2xl">+</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryCard({c, category, onSelect}) {
  return (
    <div onClick={onSelect}
      className={`p-4 rounded-xl cursor-pointer transition-all border-2 ${
        category===c.value 
          ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' 
          : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-cyan-300 dark:hover:border-cyan-700'
      }`}>
      <div className="text-2xl mb-2">{c.icon}</div>
      <div className={`font-bold text-sm mb-1 ${category===c.value ? 'text-cyan-700 dark:text-cyan-400' : 'text-slate-900 dark:text-white'}`}>
        {c.shortLabel}
      </div>
      {c.desc && <div className="text-xs text-slate-500">{c.desc}</div>}
    </div>
  );
}

function Steps({current}) {
  const steps=["Photos","Category","Details","Price & Ship","Review"];
  return (
    <div className="flex items-center mb-8 overflow-x-auto pb-2 [&::-webkit-scrollbar]:hidden w-full">
      {steps.map((s,i)=>(
        <div key={s} className="flex items-center flex-1 min-w-0">
          <div className="flex flex-col items-center flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 border-2 transition-colors ${
              i < current 
                ? 'bg-cyan-500 border-cyan-500 text-white' 
                : i === current 
                  ? 'bg-transparent border-cyan-500 text-cyan-600 dark:text-cyan-400' 
                  : 'bg-transparent border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500'
            }`}>
              {i < current ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <div className={`text-[10px] font-bold mt-2 whitespace-nowrap uppercase tracking-wider ${
              i === current ? 'text-cyan-600 dark:text-cyan-400' : i < current ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'
            }`}>
              {s}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-[2px] flex-1 mx-2 mb-5 transition-colors ${i < current ? 'bg-cyan-500' : 'bg-slate-200 dark:bg-slate-800'}`}/>
          )}
        </div>
      ))}
    </div>
  );
}

function SmartFields({cat, extraFields, setExtraFields}) {
  if(!cat) return null;
  const upd = (key,val) => setExtraFields(p=>({...p,[key]:val}));
  const toggleChip = (key,val,multi=true) => {
    if(!multi) { upd(key,val); return; }
    const cur = extraFields[key]||[];
    upd(key, cur.includes(val)?cur.filter(x=>x!==val):[...cur,val]);
  };

  const selectedMake = extraFields["make"]||"";
  const modelOptions = selectedMake && VEHICLE_MODELS[selectedMake]
    ? VEHICLE_MODELS[selectedMake]
    : Object.values(VEHICLE_MODELS).flat();

  const selectedEquipMake = extraFields["make"]||"";
  const equipModelOptions = selectedEquipMake && EQUIPMENT_MODELS[selectedEquipMake]
    ? EQUIPMENT_MODELS[selectedEquipMake]
    : Object.values(EQUIPMENT_MODELS).flat();

  return (
    <>
      {cat.fields.some(f=>f.type==="vehicle_year") && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <VehicleDropdown label="Year" placeholder="Select year" options={YEARS} value={extraFields["year"]||""} onChange={v=>upd("year",v)}/>
          <VehicleDropdown label="Make" placeholder="Select make" options={MAKES} optionLabels={MAKE_LABELS} value={extraFields["make"]||""} onChange={v=>{upd("make",v); upd("model","");}}/>
        </div>
      )}
      {cat.fields.some(f=>f.type==="vehicle_model") && (
        <div className="mb-4">
          <VehicleDropdown label="Model" placeholder={selectedMake?`Select ${makeLabel(selectedMake)} model`:"Select make first"} options={selectedMake?modelOptions:[]} value={extraFields["model"]||""} onChange={v=>upd("model",v)}/>
          {!selectedMake && <div className="text-xs text-slate-500 mt-1.5 ml-1">← Select a make to see models</div>}
        </div>
      )}
      {cat.fields.some(f=>f.type==="equipment_year") && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          <VehicleDropdown label="Year" placeholder="Select year" options={YEARS} value={extraFields["year"]||""} onChange={v=>upd("year",v)}/>
          <VehicleDropdown label="Make" placeholder="Select make" options={EQUIPMENT_MAKES} optionLabels={EQUIPMENT_MAKE_LABELS} value={extraFields["make"]||""} onChange={v=>{upd("make",v); upd("model","");}}/>
        </div>
      )}
      {cat.fields.some(f=>f.type==="equipment_model") && (
        <div className="mb-4">
          <VehicleDropdown label="Model" placeholder={selectedEquipMake?`Select ${equipmentMakeLabel(selectedEquipMake)} model`:"Select make first"} options={selectedEquipMake?equipModelOptions:[]} value={extraFields["model"]||""} onChange={v=>upd("model",v)}/>
          {!selectedEquipMake && <div className="text-xs text-slate-500 mt-1.5 ml-1">← Select a make to see models</div>}
        </div>
      )}
      {cat.fields.filter(f=>!["vehicle_year","vehicle_make","vehicle_model","equipment_year","equipment_make","equipment_model"].includes(f.type)).map(f=>(
        <FieldGroup key={f.key}>
          <Label>{f.label}</Label>
          {f.type==="text" && <Input placeholder={f.placeholder||""} value={extraFields[f.key]||""} onChange={v=>upd(f.key,v)}/>}
          {f.type==="chips" && <Chips options={f.options} selected={extraFields[f.key]||[]} onToggle={v=>toggleChip(f.key,v)}/>}
          {f.type==="toggle" && <SmallToggle label={`Yes — ${f.label}`} value={!!extraFields[f.key]} onChange={v=>upd(f.key,v)}/>}
        </FieldGroup>
      ))}
    </>
  );
}

// ── Main Layout ───────────────────────────────────────────
export default function PostListing({ isDarkMode, toggleTheme }) {
  const [step,setStep] = useState(0);
  const [photos,setPhotos] = useState([]);
  const [category,setCategory] = useState("");
  const [title,setTitle] = useState("");
  const [condition,setCondition] = useState("");
  const [description,setDescription] = useState("");
  const [extraFields,setExtraFields] = useState({});
  const [price,setPrice] = useState("");
  const [negotiable,setNegotiable] = useState(false);
  const [location,setLocation] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [shipping,setShipping] = useState("");
  const [states,setStates] = useState([]);
  const [escortType,setEscortType] = useState([]);

  // ── PPCA verified badge ──────────────────────────────────────────────
  // This is NOT a self-declared toggle anymore. Whether the badge displays is
  // determined entirely by the seller's account verification status from the
  // backend (localStorage "user".is_verified, set after they complete PPCA
  // verification). If your User model uses a different field name, update
  // `isAccountVerified` below to match it.
  let currentUser = {};
  try { currentUser = JSON.parse(localStorage.getItem("user")) || {}; } catch { currentUser = {}; }
  const isAccountVerified = Boolean(currentUser?.is_verified || currentUser?.ppca_verified);
  const ppca = isAccountVerified;

  const navigate = useNavigate(); // <-- Add this
  // State for handling backend submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted,setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(5); //

  // --- REQUIRE LOGIN TO POST A LISTING ---
  const authToken = localStorage.getItem("token");
  useEffect(() => {
    if (!authToken) {
      navigate("/login", { state: { redirectTo: "/tradelink/post-listing" } });
    }
  }, [authToken, navigate]);

  // --- AUTOMATIC REDIRECT EFFECT ---
  useEffect(() => {
    let timer;
    if (submitted) {
      setCountdown(5); // Reset countdown just in case
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate("/marketplace"); // Change this route to match your actual marketplace URL
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    // Cleanup timer if the component unmounts or user clicks "Post Another"
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [submitted, navigate]);

  const cat = CATEGORIES.find(c=>c.value===category);
  const toggleState = s => setStates(p=>p.includes(s)?p.filter(x=>x!==s):[...p,s]);
  const toggleEscort = e => setEscortType(p=>p.includes(e)?p.filter(x=>x!==e):[...p,e]);

  const canNext = [
    photos.length>0,
    category!=="",
    title.length>3 && condition!=="",
    price!=="" && shipping!=="",
    true,
  ];

  // ── BACKEND INTEGRATION SUBMIT FUNCTION ──
  const submitListing = async () => {
    setIsSubmitting(true);
    const formData = new FormData();

    formData.append("title", title);
    formData.append("category", category);
    formData.append("condition", condition);
    formData.append("description", description);
    formData.append("price", price);
    formData.append("location", location);
    formData.append("zipcode", zipcode);
    formData.append("shipping", shipping);
    formData.append("negotiable", negotiable);
    formData.append("ppca", ppca);

    formData.append("extraFields", JSON.stringify(extraFields));
    formData.append("states", JSON.stringify(states));
    formData.append("escortType", JSON.stringify(escortType));

    photos.forEach((photo) => {
      if (photo.file) {
        formData.append("images", photo.file);
      }
    });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      const response = await fetch(`${apiUrl}/api/tradelink/listings/create/`, {
        method: "POST",
        headers: authToken ? { Authorization: `Token ${authToken}` } : {},
        body: formData,
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        const errorData = await response.json();
        alert(`Error: ${JSON.stringify(errorData)}`);
      }
    } catch (error) {
      console.error("Upload failed", error);
      alert("Failed to connect to the server.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if(submitted) {
      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-md w-full animate-in fade-in zoom-in-95 duration-300">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-extrabold text-cyan-600 dark:text-cyan-400 mb-2">Listing Posted!</h2>
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
              Now visible to thousands of verified pilot car drivers & companies on PilotLinkUSA.
            </p>
            {photos[0] && <img src={photos[0].url} alt="Cover" className="w-full rounded-2xl mb-6 aspect-video object-cover shadow-sm"/>}
            
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 text-left mb-6 shadow-sm">
              <div className="font-bold text-slate-900 dark:text-white text-lg mb-1">{title}</div>
              <div className="font-extrabold text-cyan-600 dark:text-cyan-400 text-2xl mb-2">
                ${Number(price).toLocaleString()}{negotiable && <span className="font-medium text-slate-500 text-sm"> · OBO</span>}
              </div>
              <div className="text-sm text-slate-500">{cat?.label} · {condition}</div>
              <div className="text-sm text-slate-500 mt-1">{location || "Location TBD"} {zipcode ? `(${zipcode})` : ""}</div>
              {ppca && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 text-xs font-bold px-3 py-1.5 rounded-full border border-cyan-200 dark:border-cyan-800">
                  <Check className="w-3 h-3" /> PPCA Verified Seller
                </div>
              )}
            </div>
            
            <button 
              onClick={()=>{
                setSubmitted(false);
                setStep(0);
                setPhotos([]);
                setCategory("");
                setTitle("");
                setCondition("");
                setDescription("");
                setPrice("");
                setLocation("");
                setZipcode("");
                setShipping("");
                setNegotiable(false);
                setExtraFields({});
              }}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-3.5 rounded-xl transition-all shadow-sm flex items-center justify-center gap-2 mb-4"
            >
              + Post Another Listing
            </button>

            {/* Countdown visual */}
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 animate-pulse">
              Redirecting to live marketplace in <span className="font-bold text-cyan-600 dark:text-cyan-400">{countdown}</span> seconds...
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 p-4 sm:p-6 lg:p-8 pt-24 sm:pt-28">
        <div className="max-w-2xl mx-auto space-y-6">
          
          <div className="text-center md:text-left mb-8">
            <div className="text-cyan-600 dark:text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">TradeLink Marketplace</div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight mb-2">Create a Listing</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">Reach verified pilot car drivers across the US</p>
          </div>

          <Steps current={step}/>

          {/* ── STEP 0: PHOTOS ── */}
          {step===0 && (
            <Section title="Add Photos" icon="📸">
              <p className="text-sm text-slate-500 mb-4">Great photos get 3× more views. Lead with your best shot.</p>
              <PhotoZone photos={photos} onAdd={p=>setPhotos(prev=>[...prev,p])} onRemove={i=>setPhotos(prev=>prev.filter((_,idx)=>idx!==i))}/>
            </Section>
          )}

          {/* ── STEP 1: CATEGORY ── */}
          {step===1 && (
            <Section title="What are you selling?" icon="🏷️">
              <div className="text-xs font-bold uppercase tracking-widest text-cyan-600 dark:text-cyan-400 mb-3">
                🚧 Escort Vehicle Essentials
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {CATEGORIES.filter(c=>c.priority).map(c=>(
                  <CategoryCard key={c.value} c={c} category={category} onSelect={()=>{setCategory(c.value); setCondition("");}}/>
                ))}
              </div>

              <div className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3">
                More Categories
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CATEGORIES.filter(c=>!c.priority).map(c=>(
                  <CategoryCard key={c.value} c={c} category={category} onSelect={()=>{setCategory(c.value); setCondition("");}}/>
                ))}
              </div>
            </Section>
          )}

          {/* ── STEP 2: DETAILS ── */}
          {step===2 && cat && (
            <>
              <Section title="Item Details" icon={cat.icon}>
                <FieldGroup>
                  <Label>Listing Title</Label>
                  <Input placeholder={`e.g., ${cat.value==="vehicles"?"2022 Ford F-250 Pilot Car, Fully Equipped":cat.value==="lights_strobes"?"Whelen LED Amber Strobe Light Bar, Roof Mount":cat.value==="cb_radios_comm"?"Cobra 29 LX CB Radio, Like New":cat.value==="heavy_equipment"?"2019 Caterpillar 420 Backhoe Loader, 3,200 Hrs":cat.value==="personal_items"?"Craftsman Tool Chest, Barely Used":"Item name and key detail"}`}
                    value={title} onChange={setTitle}/>
                </FieldGroup>
                
                <SmartFields cat={cat} extraFields={extraFields} setExtraFields={setExtraFields}/>
                
                <FieldGroup>
                  <Label>Condition</Label>
                  <Chips options={["vehicles","heavy_equipment"].includes(cat.value) ? VEHICLE_CONDITIONS : CONDITIONS} selected={condition} onToggle={v=>setCondition(v)} multi={false}/>
                </FieldGroup>
                
                <FieldGroup>
                  <Label sub="optional but helps">Description</Label>
                  <Textarea placeholder="Describe features, history, what's included, why you're selling..." value={description} onChange={setDescription}/>
                </FieldGroup>
              </Section>

              <Section title="Pilot Car Compatibility" icon="🚧">
                <FieldGroup>
                  <Label>Escort Type</Label>
                  <Chips options={ESCORT_TYPES} selected={escortType} onToggle={toggleEscort}/>
                </FieldGroup>
                
                <FieldGroup>
                  <Label sub={`(${states.length} selected · leave empty = all states)`}>Compatible States</Label>
                  <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto bg-slate-50 dark:bg-slate-900/50 rounded-xl p-3 border border-slate-200 dark:border-slate-800">
                    {STATES_SHORT.map(s=>(
                      <div key={s} onClick={()=>toggleState(s)}
                        className={`px-2.5 py-1 rounded-md text-xs font-bold cursor-pointer transition-all border ${
                          states.includes(s) 
                            ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800' 
                            : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700 hover:border-cyan-300'
                        }`}>
                        {s}
                      </div>
                    ))}
                  </div>
                </FieldGroup>
                
                {isAccountVerified ? (
                  <div className="flex items-center justify-between gap-3 p-3.5 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800/50 rounded-xl mt-2">
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                      <span className="text-sm font-semibold text-cyan-700 dark:text-cyan-400">PPCA Verified Seller — badge will display on this listing</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-3.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl mt-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Get PPCA Verified to display a badge on your listings</span>
                    <Link to="/get-verified" className="text-xs font-bold text-cyan-600 dark:text-cyan-400 whitespace-nowrap hover:underline">
                      Get Verified →
                    </Link>
                  </div>
                )}
              </Section>
            </>
          )}

          {/* ── STEP 3: PRICE & SHIPPING ── */}
          {step===3 && (
            <>
              <Section title="Pricing" icon="💰">
                <FieldGroup>
                  <Label>Asking Price (USD)</Label>
                  <Input placeholder="0.00" value={price} onChange={setPrice} type="number" prefix="$"/>
                </FieldGroup>
                <SmallToggle label="Open to offers (OBO — Or Best Offer)" value={negotiable} onChange={setNegotiable}/>
                {price && (
                  <div className="mt-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-100 dark:border-cyan-800/50 rounded-xl p-4">
                    <div className="font-extrabold text-2xl text-cyan-700 dark:text-cyan-400">${Number(price).toLocaleString()}</div>
                    {negotiable&&<div className="text-xs text-cyan-600 dark:text-cyan-500 mt-1">+ open to offers</div>}
                  </div>
                )}
              </Section>

              <Section title="Location & Shipping" icon="📍">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <FieldGroup>
                    <Label>City, State</Label>
                    <Input placeholder="e.g., Dallas, TX" value={location} onChange={setLocation}/>
                  </FieldGroup>
                  <FieldGroup>
                    <Label>ZIP Code</Label>
                    <Input placeholder="e.g., 75201" value={zipcode} onChange={setZipcode}/>
                  </FieldGroup>
                </div>
                <div>
                  <Label>Shipping</Label>
                  <div className="flex flex-col gap-2 mt-2">
                    {SHIPPING_OPTS.map(s=>(
                      <div key={s} onClick={()=>setShipping(s)}
                        className={`p-3.5 rounded-xl cursor-pointer border-2 transition-all font-semibold text-sm ${
                          shipping===s 
                            ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20 text-cyan-700 dark:text-cyan-400' 
                            : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400 hover:border-cyan-300'
                        }`}>
                        {s==="Local Pickup Only"&&"📍 "}{s==="Ships Nationwide"&&"🚚 "}{s==="Local + Ships"&&"📍🚚 "}{s}
                      </div>
                    ))}
                  </div>
                </div>
              </Section>
            </>
          )}

          {/* ── STEP 4: REVIEW ── */}
          {step===4 && (
            <Section title="Review & Post" icon="✅">
              {photos[0] && <img src={photos[0].url} alt="" className="w-full rounded-xl mb-6 aspect-video object-cover"/>}
              
              <div className="space-y-3 divide-y divide-slate-100 dark:divide-slate-800">
                {[
                  {label:"Title",value:title},
                  {label:"Category",value:cat?.label},
                  {label:"Condition",value:condition},
                  {label:"Price",value:price?`$${Number(price).toLocaleString()}${negotiable?" · OBO":""}` : "—"},
                  {label:"Location",value:`${location||"—"} ${zipcode ? `(${zipcode})` : ""}`.trim()},
                  {label:"Shipping",value:shipping||"—"},
                  {label:"Photos",value:`${photos.length} photo${photos.length!==1?"s":""}`},
                  {label:"States",value:states.length>0?states.slice(0,8).join(", ")+(states.length>8?` +${states.length-8} more`:""):"All states"},
                  {label:"PPCA Badge",value:ppca?"✓ Verified Seller":"—"},
                ].map(r=>(
                  <div key={r.label} className="flex justify-between py-2 items-start gap-4">
                    <span className="text-sm text-slate-500 shrink-0">{r.label}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white text-right break-words">{r.value}</span>
                  </div>
                ))}
                
                {Object.entries(extraFields).filter(([,v])=>v&&(Array.isArray(v)?v.length>0:true)).map(([k,v])=>(
                  <div key={k} className="flex justify-between py-2 items-start gap-4">
                    <span className="text-sm text-slate-500 shrink-0 capitalize">{k.replace(/_/g," ")}</span>
                    <span className="text-sm font-bold text-cyan-600 dark:text-cyan-400 text-right">
                      {Array.isArray(v)?v.join(", "):v===true?"Yes":v}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/50 rounded-xl text-xs text-slate-500 leading-relaxed">
                Your listing will be visible to <span className="font-bold text-slate-700 dark:text-slate-300">all verified PilotLink users</span>. Standard seller listing fee applies per month. By posting, you agree to our Marketplace Terms of Service.
              </div>
            </Section>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-3 pt-2">
            {step > 0 && (
              <button 
                onClick={()=>setStep(s=>s-1)}
                disabled={isSubmitting}
                className="flex-1 py-3.5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-transparent text-slate-600 dark:text-slate-300 font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                ← Back
              </button>
            )}
            <button 
              onClick={()=>step < 4 ? setStep(s=>s+1) : submitListing()}
              disabled={!canNext[step] || isSubmitting}
              className={`py-3.5 rounded-xl font-bold transition-all shadow-sm flex items-center justify-center gap-2 ${
                canNext[step] 
                  ? 'flex-[2] bg-cyan-500 hover:bg-cyan-600 text-white' 
                  : 'flex-[2] bg-slate-200 dark:bg-slate-800 text-slate-400 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Posting...
                </>
              ) : step===4 ? "🚀 Post Listing" : step===3 ? "Review →" : "Continue →"}
            </button>
          </div>

          <div className="text-center text-xs text-slate-400 mt-6 font-medium">
            PilotLinkUSA · Verified Pilot Car Network
          </div>

        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 flex flex-col relative">
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
      {/* <DebugBuildBanner /> */}
      {renderContent()}
      <Footer />
    </div>
  );
}